import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Approval as ApprovalIcon,
  CallSplit as CallSplitIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Group as CcIcon,
  Notifications as NotificationsIcon,
  PlayArrow as PlayArrowIcon,
  FlagCircle as FlagCircleIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Save as SaveIcon,
  VerifiedUser as ValidateIcon,
  Webhook as WebhookIcon,
} from '@mui/icons-material';
import ReactFlow, {
  Background,
  Connection,
  Controls,
  Edge,
  Handle,
  MarkerType,
  Position,
  ReactFlowProvider,
  addEdge,
  getSmoothStepPath,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';

import { useTranslation } from 'react-i18next';

import { DESIGNER_STAGE_HEIGHT } from '../../../theme/shellMetrics';
import type {
  ProcessDefinition,
  ProcessFlow,
  ProcessFlowEdge,
  ProcessFlowNodeType,
} from '../../../api/processes';
import { processesApi, ApiError } from '../../../api';
import { translateApiError } from '../../../i18n/translateApiError';
import {
  branchOutcomes,
  configForLoadedNode,
  defaultConfigFor,
  ensureTerminalConnectivity,
  FlowCanvasEdge,
  FlowCanvasNode,
  normalizeLegacySyntheticFlow,
  serializeCanvasFlow,
  collapseBranches,
  inspectCanvasFlow,
  repairBranchOutcomes,
  withNodePositions,
} from './flowEditorModel';
import { getProcessDefinitionNodeLabel } from '../processDisplay';
import ExecutionRuleFields from './ExecutionRuleFields';

/**
 * The row that carries an approval's outcome names, inside the card's bottom edge.
 *
 * `justifyContent: 'space-between'` with the card's own horizontal padding, so the
 * two names sit on the same left/right content edges as the label above them —
 * NOT at 8%/92% of the card's width, which is where the handles are and therefore
 * where the edges run.
 */
const OUTCOME_ROW_SX = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  gap: 1,
  px: 1.5,
  pb: 1,
} as const;

/**
 * Vertical pitch between steps.
 *
 * 200 → 128, measured (owner-confirmed plan A, 2026-08-23). ReactFlow's own
 * `getViewportForBounds` gives `zoom = min(w/(bw·(1+p)), h/(bh·(1+p)))`, so on a
 * 570px canvas a 4-row branching graph at 200 was 711px tall → zoom 0.53 → the
 * card's 0.9rem title rendered at 7.7px. At 128 the same graph is 495px → zoom
 * 0.77 → 11.1px. The readability floor is 9px, which caps a 4-row graph at 612px,
 * i.e. STEP_Y ≤ 165; 128 leaves room for the alert row above the canvas.
 *
 * Below ~100 the cards touch, so this is not the lever for deeper flows — branch
 * collapsing is. Pinned by canvasBudget.test.ts.
 */
const STEP_Y = 128;

/**
 * One fitView padding. It was 0.5 on mount and 0.35 after an insert, so adding a
 * node visibly rescaled the whole graph for no reason anybody chose.
 */
const FIT_VIEW_PADDING = 0.35;

/**
 * The border a step gets when the last validation objected to it.
 *
 * `validate_process_flow` (server) and `inspectCanvasFlow` (client) both produce
 * one issue per node WITH its id, and the client used to throw them away — so the
 * error read "Fix the highlighted setup" and highlighted nothing. A promise of an
 * affordance that does not exist is worse than no affordance.
 */
/**
 * The fold/unfold control on a branch owner.
 *
 * Inside the card's own footprint, on its bottom row, for the same reason the
 * outcome names are: anything drawn below the card sits where the edges run
 * (owner screenshots 2026-08-23 18:51 and 19:28, twice).
 */
const COLLAPSE_CHIP_SX = (color: string) => ({
  display: 'block',
  width: '100%',
  mx: 0,
  px: 1.5,
  pb: 1,
  border: 0,
  bgcolor: 'transparent',
  textAlign: 'left' as const,
  cursor: 'pointer',
  font: 'inherit',
  fontSize: '0.65rem',
  fontWeight: 700,
  color,
});

const NODE_INVALID_SX = {
  borderColor: 'error.main',
  boxShadow: '0 0 0 3px rgba(211,47,47,0.14)',
};

/**
 * The canvas's floating action strip.
 *
 * It was `position: absolute; top: 8; right: 8`, which pins the LEFT edge wherever
 * the content ends — so as the row grew (a node/edge count chip, Validate, Save)
 * it ran off the container and the owner's screenshot read "Sa" where Save should
 * be. Anchoring both edges and allowing a wrap means the row can never be clipped.
 *
 * `pointerEvents: none` on the wrapper stays: the strip sits above the pane, and
 * without it every pixel of this row is a place the canvas cannot be grabbed
 * (the round-8 panning bug).
 */
const CANVAS_TOOLBAR_SX = {
  position: 'absolute' as const,
  top: 8,
  left: 8,
  right: 8,
  zIndex: 5,
  pointerEvents: 'none' as const,
  justifyContent: 'flex-end',
  flexWrap: 'wrap' as const,
  rowGap: 1,
};

/**
 * The properties panel's ONE scroll container.
 *
 * `minHeight: 0` is the load-bearing half: without it a flex child refuses to
 * shrink below its content, the column grows past the stage height, and the
 * ancestor scrolls too — two scrollbars for one list.
 */
const PANEL_SCROLL_SX = {
  flex: 1,
  minHeight: 0,
  overflow: 'auto' as const,
  pr: 1,
  m: 0,
  p: 0,
  border: 0,
  minInlineSize: 0,
};

/**
 * The step type, as a corner badge on the card rather than a coloured header row.
 *
 * The header cost 24px of padding plus a 32px avatar for information the reader
 * already has from the icon and the colour; as a badge it costs nothing vertical.
 * Kissflow labels each step with its type the same way ("Start", "Branch",
 * "Goto") — the one thing the three reference builders did that we did not.
 */
const NODE_TYPE_BADGE_SX = (color: string) => ({
  position: 'absolute' as const,
  top: -9,
  left: 10,
  px: 0.75,
  fontSize: '0.625rem',
  fontWeight: 700,
  lineHeight: '18px',
  color: 'white',
  bgcolor: color,
  borderRadius: 0.75,
  whiteSpace: 'nowrap' as const,
  pointerEvents: 'none' as const,
});

interface NodeSpec {
  type: ProcessFlowNodeType | 'timer' | 'webhook';
  icon: 'PlayArrow' | 'FlagCircle' | 'Approval' | 'Notifications' | 'CallSplit' | 'Group' | 'HourglassEmpty' | 'Webhook';
  color: string;
}

/** Engine v1 is deliberately six nodes. Timer, webhook, subprocess and parallel
 * gateways are not hidden behind a feature flag: they are not insertable at all. */
const NODE_SPECS: Array<NodeSpec & { type: ProcessFlowNodeType }> = [
  { type: 'start', icon: 'PlayArrow', color: '#4caf50' },
  { type: 'end', icon: 'FlagCircle', color: '#757575' },
  { type: 'approval', icon: 'Approval', color: '#1565c0' },
  { type: 'notification', icon: 'Notifications', color: '#6a1b9a' },
  { type: 'condition', icon: 'CallSplit', color: '#ef6c00' },
  { type: 'cc', icon: 'Group', color: '#546e7a' },
];
const LEGACY_DISPLAY_SPECS: NodeSpec[] = [
  { type: 'timer', icon: 'HourglassEmpty', color: '#00897b' },
  { type: 'webhook', icon: 'Webhook', color: '#5c6bc0' },
];
const INSERTABLE_SPECS = NODE_SPECS.filter(
  (spec) => spec.type !== 'start' && spec.type !== 'end',
);

function getIcon(icon: NodeSpec['icon']) {
  switch (icon) {
    case 'PlayArrow': return <PlayArrowIcon />;
    case 'FlagCircle': return <FlagCircleIcon />;
    case 'Approval': return <ApprovalIcon />;
    case 'Notifications': return <NotificationsIcon />;
    case 'CallSplit': return <CallSplitIcon />;
    case 'Group': return <CcIcon />;
    case 'HourglassEmpty': return <HourglassEmptyIcon />;
    case 'Webhook': return <WebhookIcon />;
  }
}

function specForType(type: string): NodeSpec {
  return [...NODE_SPECS, ...LEGACY_DISPLAY_SPECS]
    .find((spec) => spec.type === type) ?? NODE_SPECS[3];
}

function edgeType(edge: ProcessFlowEdge): ProcessFlowEdge['type'] {
  return edge.type ?? 'smoothstep';
}

interface CanvasProps {
  definition: ProcessDefinition;
  onBack: () => void;
  onSaved: (updated: ProcessDefinition) => void;
  onDirtyChange?: (dirty: boolean) => void;
}

function Canvas({ definition, onBack, onSaved, onDirtyChange }: CanvasProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const readOnly = definition.status !== 'draft';
  const { fitView } = useReactFlow();
  const fitViewRef = useRef(fitView);
  fitViewRef.current = fitView;

  const initial = useMemo((): { nodes: FlowCanvasNode[]; edges: FlowCanvasEdge[] } => {
    const rawFlow = definition.flow as ProcessFlow | null | undefined;
    const normalized = rawFlow?.nodes?.length
      ? normalizeLegacySyntheticFlow(rawFlow)
      : rawFlow;
    // Positions LAST, so a legacy/seeded flow that normalisation just expanded also gets
    // coordinates. Without this ReactFlow throws while building node internals.
    const flow = normalized?.nodes?.length ? withNodePositions(normalized) : normalized;
    if (flow?.nodes?.length) {
      return {
        nodes: flow.nodes.map((node) => {
          const type = node.type as ProcessFlowNodeType;
          return {
            id: node.id,
            type: type === 'condition' ? 'diamond' : 'custom',
            position: node.position,
            data: {
              id: node.id,
              label: node.data?.label ?? '',
              displayLabel: getProcessDefinitionNodeLabel(t, definition, node),
              spec: specForType(type),
              nodeType: type,
              // Never merge defaults into persisted config: legacy approver_role,
              // manager_level and expression are executable contracts. Adding a
              // resolver/condition key would take precedence and silently change them.
              // Defaults belong only to newly inserted nodes.
              config: configForLoadedNode(
                type,
                node.data?.config,
                flow.contract_version !== 1,
              ),
            },
          };
        }),
        edges: flow.edges.map((edge) => ({
          id: edge.id,
          source: edge.source,
          target: edge.target,
          sourceHandle: edge.sourceHandle ?? undefined,
          targetHandle: edge.targetHandle ?? undefined,
          type: 'custom',
          persistedType: edgeType(edge),
          label: edge.label ?? undefined,
          data: edge.data ? { ...edge.data } : undefined,
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: theme.palette.primary.main,
            width: 20,
            height: 20,
          },
        })),
      };
    }

    const startSpec = specForType('start');
    const endSpec = specForType('end');
    return {
      nodes: [
        {
          id: 'start-node',
          type: 'custom',
          position: { x: 0, y: 0 },
          data: {
            id: 'start-node',
            label: t('processes:flowEditor.nodeSpecs.start'),
            spec: startSpec,
            nodeType: 'start',
            config: defaultConfigFor('start'),
          },
        },
        {
          id: 'end-node',
          type: 'custom',
          position: { x: 0, y: 220 },
          data: {
            id: 'end-node',
            label: t('processes:flowEditor.nodeSpecs.end'),
            spec: endSpec,
            nodeType: 'end',
            config: defaultConfigFor('end'),
          },
        },
      ],
      edges: [
        {
          id: 'edge-start-end',
          source: 'start-node',
          target: 'end-node',
          type: 'custom',
          persistedType: 'smoothstep',
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: theme.palette.primary.main,
            width: 20,
            height: 20,
          },
        },
      ],
    };
  }, [definition, t, theme.palette.primary.main]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initial.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initial.edges);

  // Fit ONCE after the browser has measured the custom nodes (owner: "workflow 的最后都没有
  // end 节点", 2026-08-24 — they were rendered, just below the stage).
  //
  // `<ReactFlow fitView>` fits at mount, and a `custom`/`diamond` node has no explicit
  // width or height, so at that moment every node measures 0×0 and the computed bounding
  // box is meaningless. The graph then sat at its raw coordinates, unzoomed, pushed to the
  // bottom-right with the deepest rank — the two end nodes — outside the visible stage.
  // `handleInsertOnEdge` already worked around the same timing with a 60ms defer.
  //
  // A REF latch, not state, and node COUNT rather than the array: `useNodesState` hands back
  // a new array identity on every drag tick, so depending on `nodes` would re-fit the canvas
  // mid-drag and fight the user for the viewport.
  const hasFittedRef = useRef(false);
  const nodeCount = nodes.length;
  useEffect(() => {
    if (hasFittedRef.current || nodeCount === 0) return;
    hasFittedRef.current = true;
    const timer = setTimeout(
      () => fitViewRef.current({ padding: FIT_VIEW_PADDING, duration: 0 }),
      60,
    );
    return () => clearTimeout(timer);
  }, [nodeCount]);
  const [dirty, setDirty] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [edgeForMenuId, setEdgeForMenuId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  /**
   * Steps the last validation objected to. Empty until the user asks for a verdict
   * — a brand new draft must not be told it is broken before it has been touched
   * (owner screenshot 2026-08-23 19:48, an untouched "Untitled Process").
   */
  const [invalidNodeIds, setInvalidNodeIds] = useState<string[]>([]);
  /**
   * Branch owners the reader has folded. VIEW STATE ONLY — it never reaches the
   * persisted flow, so folding a branch must not dirty the form and a save must
   * still write every step (collapseWiring.test.ts pins both).
   */
  const [collapsedIds, setCollapsedIds] = useState<ReadonlySet<string>>(new Set());
  const collapsedIdsRef = useRef<ReadonlySet<string>>(collapsedIds);
  collapsedIdsRef.current = collapsedIds;
  const invalidNodeIdsRef = useRef<string[]>([]);
  invalidNodeIdsRef.current = invalidNodeIds;

  const markDirty = useCallback(() => {
    if (readOnly) return;
    setDirty(true);
    onDirtyChange?.(true);
  }, [onDirtyChange, readOnly]);

  /**
   * ReactFlow rebuilds every node whenever the `nodeTypes` object identity changes, and the
   * renderers are memoised on their dependencies — so ONE unstable dependency remounts the
   * whole canvas on every render and the node you are dragging is replaced mid-gesture. That is
   * the "画布无法拖拽" the owner reported.
   *
   * `t` from `useTranslation()` is not referentially stable, and `selectedNodeId` changes the
   * moment you click a node. Both are read through refs so the renderers can be memoised on
   * genuinely stable values only.
   */
  const tRef = useRef(t);
  tRef.current = t;
  const selectedNodeIdRef = useRef<string | null>(null);
  selectedNodeIdRef.current = selectedNodeId;

  const handleNodesChange = useCallback((changes: Parameters<typeof onNodesChange>[0]) => {
    onNodesChange(changes);
    if (changes.some((change) => change.type !== 'select' && change.type !== 'dimensions')) {
      markDirty();
    }
  }, [markDirty, onNodesChange]);

  /**
   * Read-only still needs a change handler (owner: "readonly 的时候画布不能拖拽… 后面画布被
   * 锁死", 2026-08-24).
   *
   * Passing `onNodesChange={undefined}` looked like the safe way to make a published process
   * immutable, and it froze the canvas instead. ReactFlow delivers node MEASUREMENTS through
   * this same callback as `dimensions` changes; with no handler its store never learns any node
   * has a size, so `fitView` computes an empty bounding box and the viewport ends up wherever
   * the raw coordinates fall. `select` changes are dropped the same way, which is why the pane
   * felt dead after the first interaction.
   *
   * So: forward the non-destructive changes, drop the destructive ones. `position` is forwarded
   * on purpose — the owner asked to be able to rearrange nodes while reading a published
   * process, and moving one changes only the layout of a graph that cannot be saved from here.
   * `markDirty` is deliberately NOT called, so re-arranging never claims unsaved work.
   */
  const handleReadOnlyNodesChange = useCallback((changes: Parameters<typeof onNodesChange>[0]) => {
    onNodesChange(changes.filter((change) => change.type === 'dimensions'
      || change.type === 'select'
      || change.type === 'position'));
  }, [onNodesChange]);

  /** Measurements only. Read-only must never gain or lose an edge. */
  const handleReadOnlyEdgesChange = useCallback((changes: Parameters<typeof onEdgesChange>[0]) => {
    onEdgesChange(changes.filter((change) => change.type === 'select'));
  }, [onEdgesChange]);

  const handleEdgesChange = useCallback((changes: Parameters<typeof onEdgesChange>[0]) => {
    onEdgesChange(changes);
    // The SECOND surface for the same P0. ReactFlow lets the user select the
    // "approved" line and press Delete, and this handler forwards `remove` changes
    // verbatim — so an outcome ends up wired to nothing without any node being
    // deleted. There is no deleted node to re-point from here, which is exactly why
    // the invariant needs a backstop rather than only a smarter delete.
    if (changes.some((change) => change.type === 'remove')) {
      setEdges((current) => repairBranchOutcomes(
        nodesRef.current as FlowCanvasNode[],
        current as FlowCanvasEdge[],
      ));
    }
    if (changes.some((change) => change.type !== 'select')) markDirty();
  }, [markDirty, onEdgesChange, setEdges]);

  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;
  const edgesRef = useRef(edges);
  edgesRef.current = edges;

  const selectedNode = nodes.find((node) => node.id === selectedNodeId) ?? null;

  const markerEnd = useCallback(() => ({
    type: MarkerType.ArrowClosed,
    color: theme.palette.primary.main,
    width: 20,
    height: 20,
  }), [theme.palette.primary.main]);

  const makeEdge = useCallback((
    id: string,
    source: string,
    target: string,
    overrides: Partial<FlowCanvasEdge> = {},
  ): FlowCanvasEdge => ({
    id,
    source,
    target,
    type: 'custom',
    persistedType: 'smoothstep',
    markerEnd: markerEnd(),
    ...overrides,
  }), [markerEnd]);

  const onConnect = useCallback((params: Edge | Connection) => {
    markDirty();
    setEdges((current) => {
      // One executable destination per explicit branch handle. Reconnecting a
      // handle replaces its prior edge rather than creating an ambiguous duplicate.
      const withoutPrior = params.sourceHandle
        ? current.filter((edge) => !(
            edge.source === params.source && edge.sourceHandle === params.sourceHandle
          ))
        : current;
      return addEdge({
        ...params,
        type: 'custom',
        persistedType: 'smoothstep',
        markerEnd: markerEnd(),
      } as FlowCanvasEdge, withoutPrior);
    });
  }, [markDirty, markerEnd, setEdges]);

  const addFlowNode = useCallback((edgeId: string, spec: NodeSpec & { type: ProcessFlowNodeType }) => {
    const currentEdge = edgesRef.current.find((edge) => edge.id === edgeId) as FlowCanvasEdge | undefined;
    if (!currentEdge) return;
    const sourceNode = nodesRef.current.find((node) => node.id === currentEdge.source);
    const targetNode = nodesRef.current.find((node) => node.id === currentEdge.target);
    if (!sourceNode || !targetNode) return;

    const newId = `flow-${Date.now()}`;
    const newNode: FlowCanvasNode = {
      id: newId,
      type: spec.type === 'condition' ? 'diamond' : 'custom',
      position: {
        x: (sourceNode.position.x + targetNode.position.x) / 2,
        y: sourceNode.position.y + STEP_Y,
      },
      data: {
        id: newId,
        label: t(`processes:flowEditor.nodeSpecs.${spec.type}`),
        spec,
        nodeType: spec.type,
        config: defaultConfigFor(spec.type),
      },
    };

    const shifted = nodesRef.current.map((node) =>
      node.position.y >= targetNode.position.y
        ? { ...node, position: { ...node.position, y: node.position.y + STEP_Y } }
        : node,
    );
    markDirty();
    setNodes([...shifted, newNode]);

    const incoming = makeEdge(
      `edge-${currentEdge.source}-${newId}`,
      currentEdge.source,
      newId,
      {
        sourceHandle: currentEdge.sourceHandle,
        targetHandle: undefined,
        persistedType: currentEdge.persistedType,
        label: currentEdge.label,
        data: currentEdge.data,
      },
    );
    const outcomes = branchOutcomes(spec.type);
    const outgoing = outcomes.length
      ? outcomes.map((outcome) => makeEdge(
          `edge-${newId}-${outcome}-${currentEdge.target}`,
          newId,
          currentEdge.target,
          {
            sourceHandle: outcome,
            targetHandle: currentEdge.targetHandle,
            label: outcome === 'approved'
              ? t('common:status.approved')
              : outcome === 'rejected'
                ? t('common:status.rejected')
                : outcome === 'true'
                  ? t('common:answers.yes')
                  : t('common:answers.no'),
          },
        ))
      : [makeEdge(
          `edge-${newId}-${currentEdge.target}`,
          newId,
          currentEdge.target,
          { targetHandle: currentEdge.targetHandle },
        )];

    setEdges((current) => current.filter((edge) => edge.id !== edgeId).concat(incoming, outgoing));
    setSelectedNodeId(newId);
    setTimeout(() => fitViewRef.current({ padding: FIT_VIEW_PADDING, duration: 350 }), 60);
  }, [makeEdge, markDirty, setEdges, setNodes, t]);

  const handleDeleteNode = useCallback((nodeId: string) => {
    const node = nodesRef.current.find((candidate) => candidate.id === nodeId);
    if (!node || node.data.nodeType === 'start' || node.data.nodeType === 'end') return;
    markDirty();
    const incoming = edgesRef.current.filter((edge) => edge.target === nodeId);
    const outgoing = edgesRef.current.filter((edge) => edge.source === nodeId);
    setNodes((current) => current.filter((candidate) => candidate.id !== nodeId));
    setEdges((current) => {
      const remaining = current.filter((edge) => edge.source !== nodeId && edge.target !== nodeId);
      // EVERY incoming edge is re-pointed, not just the 1-in/1-out case.
      //
      // The old rule bridged only when the deleted step had exactly one edge in and
      // one out, reasoning that a branching step "cannot" be bridged because that
      // would mean picking one of ITS outcomes. True — but the cost of not picking
      // landed on the PREDECESSOR: deleting a condition wired under an approval's
      // `approved` handle left that outcome pointing at nothing, so the approval
      // visibly lost a line (owner, P0, 2026-08-23).
      //
      // Each incoming edge keeps its own handle/label and lands on the deleted
      // node's FIRST successor. Any further successors lose their only inbound edge
      // and become orphans, which `Validate` reports as unreachable — visible and
      // fixable, rather than an outcome silently wired to nothing.
      const firstSuccessor = outgoing.find((edge) => edge.target !== nodeId)?.target;
      const bridged = firstSuccessor
        ? remaining.concat(incoming
          .filter((edge) => edge.source !== firstSuccessor)
          .map((edge) => makeEdge(
            `edge-${edge.source}-${firstSuccessor}-${edge.sourceHandle ?? ''}`,
            edge.source,
            firstSuccessor,
            {
              sourceHandle: edge.sourceHandle,
              targetHandle: outgoing[0].targetHandle,
              label: edge.label,
              data: edge.data,
            },
          )))
        : remaining;

      // Either way, the terminals must stay wired — including the case the owner hit, where the
      // last middle step is removed and start/end must fall back to a direct connection.
      const survivingNodes = nodesRef.current
        .filter((candidate) => candidate.id !== nodeId)
        .map((candidate) => ({
          id: candidate.id,
          type: candidate.data.nodeType,
          position: candidate.position,
          data: { label: candidate.data.label },
        })) as ProcessFlow['nodes'];
      const required = ensureTerminalConnectivity(
        survivingNodes,
        bridged.map((edge) => ({
          id: edge.id,
          source: edge.source,
          target: edge.target,
          sourceHandle: edge.sourceHandle ?? null,
          targetHandle: edge.targetHandle ?? null,
        })),
      );
      const existing = new Set(bridged.map((edge) => edge.id));
      const wired = bridged.concat(
        required
          .filter((edge) => !existing.has(edge.id))
          .map((edge) => makeEdge(edge.id, edge.source, edge.target)),
      );
      // Backstop: whatever the shape was, no branch outcome may be left without a
      // destination (session-discipline §12 — the front fix picks a target, this
      // guarantees the invariant).
      return repairBranchOutcomes(
        nodesRef.current.filter((candidate) => candidate.id !== nodeId) as FlowCanvasNode[],
        wired as FlowCanvasEdge[],
      );
    });
    if (selectedNodeIdRef.current === nodeId) setSelectedNodeId(null);
  }, [makeEdge, markDirty, setEdges, setNodes]);

  const updateSelectedConfig = useCallback((next: Record<string, unknown>) => {
    if (!selectedNodeId) return;
    markDirty();
    setNodes((current) => current.map((node) => node.id === selectedNodeId
      ? { ...node, data: { ...node.data, config: next } }
      : node));
  }, [markDirty, selectedNodeId, setNodes]);

  const handleSave = useCallback(async () => {
    if (readOnly) return;
    setSaving(true);
    setToast(null);
    try {
      const flow = serializeCanvasFlow(nodes as FlowCanvasNode[], edges as FlowCanvasEdge[]);
      const updated = await processesApi.processDefinitionsApi.saveFlow(definition.id, flow);
      setToast({ message: t('processes:flowEditor.toast.flowSaved'), severity: 'success' });
      setDirty(false);
      onDirtyChange?.(false);
      onSaved(updated);
    } catch (error) {
      setToast({
        message: error instanceof ApiError
          ? translateApiError(error, t)
          : t('processes:flowEditor.toast.saveFailed'),
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  }, [definition.id, edges, nodes, onDirtyChange, onSaved, readOnly, t]);

  const handleValidate = useCallback(() => {
    const { issues: validationIssues, nodeIds } = inspectCanvasFlow(
      nodes as FlowCanvasNode[],
      edges as FlowCanvasEdge[],
    );
    // Keep the ids the walk already produced. Dropping them is what left the
    // canvas saying "Fix the highlighted setup" with nothing highlighted.
    setInvalidNodeIds(nodeIds);
    if (validationIssues.includes('missing_nodes')) {
      setToast({ message: t('processes:flowEditor.toast.validationMissingNodes'), severity: 'error' });
      return;
    }
    if (validationIssues.includes('unreachable_node')) {
      const starts = nodes.filter((node) => node.data.nodeType === 'start');
      const visited = new Set<string>();
      const queue = starts.length === 1 ? [starts[0].id] : [];
      while (queue.length) {
        const current = queue.shift()!;
        if (visited.has(current)) continue;
        visited.add(current);
        edges.filter((edge) => edge.source === current).forEach((edge) => queue.push(edge.target));
      }
      setToast({
        message: t('processes:flowEditor.toast.validationUnreachable', {
          count: nodes.filter((node) => !visited.has(node.id)).length,
        }),
        severity: 'error',
      });
      return;
    }
    if (validationIssues.includes('branch_outcome')) {
      setToast({ message: t('processes:flowEditor.toast.validationMissingOutcomes'), severity: 'error' });
      return;
    }
    if (validationIssues.length) {
      setToast({ message: t('errors:codes.invalid_process_flow'), severity: 'error' });
      return;
    }
    setToast({ message: t('processes:flowEditor.toast.validationPassed'), severity: 'success' });
  }, [edges, nodes, t]);

  const closeMenu = () => {
    setAnchorEl(null);
    setEdgeForMenuId(null);
  };

  const CustomNodeRenderer = useCallback(({ data, id, selected }: any) => {
    const spec = data.spec as NodeSpec;
    const isStart = data.nodeType === 'start';
    const isEnd = data.nodeType === 'end';
    const outcomes = branchOutcomes(data.nodeType);
    const color = isStart
      ? theme.palette.success.main
      : isEnd
        ? theme.palette.grey[700]
        : spec.color;

    if (isStart || isEnd) {
      return (
        <Box sx={{ textAlign: 'center', position: 'relative' }}>
          {isEnd && <Handle type="target" position={Position.Top} style={{ background: color, width: 12, height: 12, top: -6 }} />}
          <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: isStart ? color : 'background.paper', border: `3px solid ${color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <Avatar sx={{ bgcolor: 'transparent', color: isStart ? 'white' : color, width: 50, height: 50 }}>
              {getIcon(spec.icon)}
            </Avatar>
          </Box>
          <Typography variant="caption" sx={{ mt: 1, display: 'block', fontWeight: 700 }}>{data.displayLabel ?? data.label}</Typography>
          {isStart && <Handle type="source" position={Position.Bottom} style={{ background: color, width: 12, height: 12, bottom: -6 }} />}
        </Box>
      );
    }

    return (
      <Box sx={{ position: 'relative', '&:hover .node-delete-btn': { opacity: 1 } }}>
        <Handle type="target" position={Position.Top} style={{ background: color, width: 12, height: 12, top: -6 }} />
        <Box sx={{
          width: 240, borderRadius: 2, overflow: 'hidden', border: '2px solid',
          bgcolor: 'background.paper', cursor: 'pointer',
          // `selected` first: the step you clicked is the one you are looking at.
          // Read through a REF, not a dep — putting `invalidNodeIds` in this
          // callback's dependency array would rebuild `nodeTypes` on every
          // validation and remount every node mid-drag (the round-7 bug).
          ...(selected
            ? { borderColor: color }
            : invalidNodeIdsRef.current.includes(id) ? NODE_INVALID_SX : { borderColor: 'divider' }),
        }}>
          {!readOnly && (
            <IconButton
              size="small"
              className="node-delete-btn"
              aria-label={tRef.current('processes:flowEditor.deleteNode')}
              // Inside the card's top-right corner: at `right: -32` it hung outside the node
              // box, where the canvas clips it and it can cover the neighbouring node.
              sx={{ position: 'absolute', top: 4, right: 4, width: 24, height: 24, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', opacity: 0, '&:hover': { bgcolor: 'error.main', color: 'white' }, zIndex: 10 }}
              onClick={(event) => { event.stopPropagation(); handleDeleteNode(id); }}
            >
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
          {/* TWO rows, not three (plan A). The card used to carry the type name in a
              coloured header, the step's own name, AND the type's description — three
              text rows plus 24px of header padding, which is what made a 4-row graph
              711px tall and its 0.9rem title render at 7.7px.
              The type is a corner badge now (Kissflow ships exactly this and it was
              the one reference feature we lacked) and the description moved into the
              properties panel, where it can be read at full size. */}
          <Box sx={{ px: 1.5, py: 1.25, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: `${color}1f`, color, width: 26, height: 26 }}>{getIcon(spec.icon)}</Avatar>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {data.displayLabel ?? data.label}
            </Typography>
          </Box>
          <Box sx={NODE_TYPE_BADGE_SX(color)}>
            {tRef.current(`processes:flowEditor.nodeSpecs.${spec.type}`)}
          </Box>
          {/* Only a step that OWNS a branch can be folded — two outcomes is what
              makes one. A chevron on a linear step would toggle nothing.
              Everything here is read through refs: `collapsedIds` changes on every
              click, and putting it in this callback's dep array would rebuild
              `nodeTypes` and let ReactFlow replace every node mid-gesture (round 7). */}
          {outcomes.length > 1 && (
            <Box
              component="button"
              type="button"
              aria-expanded={!collapsedIdsRef.current.has(id)}
              sx={COLLAPSE_CHIP_SX(color)}
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation();
                toggleCollapsedRef.current(id);
              }}
            >
              {collapsedIdsRef.current.has(id)
                ? tRef.current('processes:flowEditor.collapsedSteps', {
                  count: hiddenByNodeRef.current.get(id) ?? 0,
                })
                : tRef.current('processes:flowEditor.collapseBranches')}
            </Box>
          )}
          {/* The outcome names live INSIDE the card, on its bottom row.
              They used to sit at `bottom: -20`, centred on their handle — and the
              edge leaves that same handle heading straight down, so the line ran
              through the text ("approved和rejected字被线条挡住", owner 2026-08-23).
              Round 8 had moved the handles to the card's corners to stop the two
              ROUTES overprinting each other; that fix is kept (the handles below
              still use 8%/92%), and only the text moved.
              Inside the card they also align to its own content edges rather than
              floating at 8%/92% of its width (svdy-ui §15). */}
          {outcomes.length > 0 && (
            <Box sx={OUTCOME_ROW_SX}>
              {outcomes.map((outcome) => (
                <Typography
                  key={outcome}
                  variant="caption"
                  sx={{ fontWeight: 700, fontSize: '0.65rem', color, whiteSpace: 'nowrap' }}
                >
                  {tRef.current(`processes:processDesigner.executionRules.outcomes.${outcome}`)}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
        {outcomes.length === 0 ? (
          <Handle type="source" position={Position.Bottom} style={{ background: color, width: 12, height: 12, bottom: -6 }} />
        ) : outcomes.map((outcome, index) => (
          <Handle
            key={outcome}
            id={outcome}
            type="source"
            position={Position.Bottom}
            style={{
              background: color,
              width: 12,
              height: 12,
              bottom: -6,
              left: index === 0 ? '8%' : '92%',
            }}
          />
        ))}
      </Box>
    );
  }, [handleDeleteNode, readOnly, theme]);

  const DiamondNodeRenderer = useCallback(({ data, id, selected }: any) => {
    const color = theme.palette.warning.main;
    return (
      <Box sx={{ textAlign: 'center', position: 'relative', py: 2, pb: 1, '&:hover .node-delete-btn': { opacity: 1 } }}>
        <Handle type="target" position={Position.Top} style={{ background: color, width: 10, height: 10, top: -5 }} />
        <Box sx={{ width: 80, height: 80, bgcolor: color, border: '3px solid', borderColor: selected ? 'warning.dark' : 'common.white', transform: 'rotate(45deg)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', margin: '0 auto' }}>
          <Box sx={{ transform: 'rotate(-45deg)', color: 'white' }}><CallSplitIcon sx={{ fontSize: '1.2rem' }} /></Box>
        </Box>
        <Typography
          variant="body2"
          sx={{
            mt: 3.5,
            fontWeight: 600,
            bgcolor: color,
            color: 'white',
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            // Was `maxWidth: 120` + nowrap + ellipsis, which truncated any real condition name
            // (owner 2026-08-23: "不能全展开"). A condition's label is the one thing that says
            // what the branch tests, so it wraps to two lines instead of being cut.
            maxWidth: 200,
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            margin: '16px auto 0',
            fontSize: '0.75rem',
            lineHeight: 1.35,
          }}
        >
          {data.displayLabel ?? data.label}
        </Typography>
        {!readOnly && (
          <IconButton
            size="small"
            className="node-delete-btn"
            aria-label={tRef.current('processes:flowEditor.deleteNode')}
            // INSIDE the node's own box. At `right: -32` it sat outside the bounding box, so it
            // was clipped by the canvas and could overlap the node beside it.
            sx={{ position: 'absolute', top: 0, right: 0, width: 24, height: 24, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', opacity: 0, '&:hover': { bgcolor: 'error.main', color: 'white' }, zIndex: 10 }}
            onClick={(event) => { event.stopPropagation(); handleDeleteNode(id); }}
          >
            <DeleteIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )}
        {/* Yes on the RIGHT tip, No on the LEFT tip — the conventional decision-diamond shape,
            and it matches the layout, which fans same-rank siblings sideways.
            The old version put both handles at 30%/70% of the WRAPPER, whose width is set by the
            label (up to 200px) rather than by the 80px diamond, so the two dots landed away from
            the diamond and read as detached (owner: "condition的左右节点显示问题"). */}
        {([
          { outcome: 'false', side: Position.Left },
          { outcome: 'true', side: Position.Right },
        ] as const).map(({ outcome, side }) => (
          <React.Fragment key={outcome}>
            <Handle
              id={outcome}
              type="source"
              position={side}
              style={{
                background: color,
                width: 10,
                height: 10,
                top: 56,
                [side === Position.Left ? 'left' : 'right']: 'calc(50% - 56px)',
              }}
            />
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                // Clear of the dot, not on it: at `calc(50% + 52px)` the text started 4px inside
                // the handle and the two overprinted each other on the diamond's tips.
                top: 44,
                [side === Position.Left ? 'right' : 'left']: 'calc(50% + 68px)',
                whiteSpace: 'nowrap',
                fontWeight: 700,
                fontSize: '0.7rem',
                color: 'warning.dark',
                // The edge leaves the handle HORIZONTALLY at y≈61 and this label's
                // line box reaches 62.6, so the line ran through the text (owner
                // screenshot 2026-08-23 19:28). I had computed a 2px clearance from
                // an ESTIMATED line-height, and the estimate was the whole margin.
                //
                // An opaque background is the answer every flowchart tool uses for
                // an edge label, and it does not depend on a clearance being right.
                // The diamond has no inside to move into — that fix belongs to the
                // approval card, which has one.
                bgcolor: 'background.paper',
                px: 0.5,
                borderRadius: 0.5,
              }}
            >
              {tRef.current(`processes:processDesigner.executionRules.outcomes.${outcome}`)}
            </Typography>
          </React.Fragment>
        ))}
      </Box>
    );
  }, [handleDeleteNode, readOnly, theme]);

  const CustomEdgeRenderer = useCallback((props: any) => {
    const [path, labelX, labelY] = getSmoothStepPath(props);
    return (
      <>
        <path id={props.id} style={{ stroke: theme.palette.primary.main, strokeWidth: 3 }} className="react-flow__edge-path" d={path} markerEnd={props.markerEnd} />
        {/* 220, not 150: the chip and the +28px insert button shared a 150px box, so any branch
            label longer than a few characters was clipped by the foreignObject. */}
        <foreignObject width={64} height={40} x={labelX - 32} y={labelY - 20} requiredExtensions="http://www.w3.org/1999/xhtml" style={{ overflow: 'visible' }}>
          {/* `pointerEvents: none` on the wrapper. This box sits in the edge layer ABOVE the pane,
              so a div that accepts events blocks canvas panning everywhere an edge label lands —
              the owner's "画布无法点击挪动". Only the insert button opts back in.

              The branch chip that used to live here is gone: each outcome is now named ON its
              source node, so the chip was a second copy of the same word — and two of them landed
              on top of each other ("Appro..Rejected"). Narrower box = less of the pane covered. */}
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', pointerEvents: 'none' }}>
            {!readOnly && (
              <Tooltip title={tRef.current('processes:flowEditor.addNode')}>
                <IconButton size="small" sx={{ pointerEvents: 'auto', width: 28, height: 28, bgcolor: 'primary.main', color: 'white', border: '2px solid white', '&:hover': { bgcolor: 'primary.dark' } }} onClick={(event) => { setAnchorEl(event.currentTarget as HTMLElement); setEdgeForMenuId(props.id); }}>
                  <AddIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
            )}
          </div>
        </foreignObject>
      </>
    );
  }, [readOnly, theme.palette.primary.main]);

  const nodeTypes = useMemo(() => ({
    custom: CustomNodeRenderer,
    diamond: DiamondNodeRenderer,
  }), [CustomNodeRenderer, DiamondNodeRenderer]);
  const edgeTypes = useMemo(() => ({ custom: CustomEdgeRenderer }), [CustomEdgeRenderer]);

  /**
   * What ReactFlow draws. `nodes`/`edges` stay the model — `serializeCanvasFlow`
   * reads those, and running it off this view would silently delete every folded
   * step on the next save.
   */
  const { nodes: visibleNodes, edges: visibleEdges, hiddenByNode } = useMemo(
    () => collapseBranches(nodes as FlowCanvasNode[], edges as FlowCanvasEdge[], collapsedIds),
    [nodes, edges, collapsedIds],
  );
  const hiddenByNodeRef = useRef(hiddenByNode);
  hiddenByNodeRef.current = hiddenByNode;

  const toggleCollapsed = useCallback((nodeId: string) => {
    setCollapsedIds((current) => {
      // es5 target, no downlevelIteration: a Set is not spreadable here (§12.9).
      const next = new Set(Array.from(current));
      if (next.has(nodeId)) next.delete(nodeId); else next.add(nodeId);
      return next;
    });
  }, []);
  const toggleCollapsedRef = useRef(toggleCollapsed);
  toggleCollapsedRef.current = toggleCollapsed;

  const selectedConfig = selectedNode?.data.config ?? {};

  return (
    <Box sx={{ ...DESIGNER_STAGE_HEIGHT, display: 'flex', flexDirection: 'column' }}>
      {/* No second header row. It repeated the process name and the Back arrow that the wizard
          header already shows one row above (owner 2026-08-23: "标题跟顶部的有重复"), and it cost
          ~70px of canvas height. The two real actions live on the canvas they act on. */}
      {/* No read-only banner here. `ProcessDesigner` renders one above the stepper for every
          step, so this produced TWO stacked banners on the Workflow step (owner screenshot
          2026-08-24) — the same sentence twice, one of them without the action. */}
      {toast && <Alert severity={toast.severity} sx={{ mb: 1 }} onClose={() => setToast(null)}>{toast.message}</Alert>}

      <Grid container spacing={2} sx={{ flex: 1, minHeight: 0 }}>
        <Grid item xs={12} md={selectedNode ? 8 : 12} sx={{ height: '100%' }}>
          <Paper sx={{ height: '100%', borderRadius: 2, border: '1px solid', borderColor: 'divider', position: 'relative', overflow: 'hidden' }}>
            <ReactFlow
              nodes={visibleNodes}
              edges={visibleEdges}
              onNodesChange={readOnly ? handleReadOnlyNodesChange : handleNodesChange}
              onEdgesChange={readOnly ? handleReadOnlyEdgesChange : handleEdgesChange}
              onConnect={readOnly ? undefined : onConnect}
              // Draggable even when read-only: rearranging a published graph to read it is not
              // an edit, and there is no Save on this surface. What read-only forbids is
              // structure — `nodesConnectable` / `edgesUpdatable` stay off, and the read-only
              // change handlers drop every `remove` and `add`.
              nodesDraggable
              nodesConnectable={!readOnly}
              edgesUpdatable={!readOnly}
              elementsSelectable
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              onNodeClick={(_, node) => setSelectedNodeId(node.id)}
              onPaneClick={() => setSelectedNodeId(null)}
              fitView
              fitViewOptions={{ padding: FIT_VIEW_PADDING }}
              proOptions={{ hideAttribution: true }}
              minZoom={0.3}
              maxZoom={1.5}
            >
              <Background gap={20} size={1.5} color={theme.palette.grey[300]} />
              <Controls />
            </ReactFlow>
            {/* Floating over the canvas, top-right: the actions sit on the thing they act on, and
                the count is context rather than a heading. `pointerEvents: none` on the wrapper so
                this strip never becomes another place the pane cannot be grabbed. */}
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={CANVAS_TOOLBAR_SX}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ bgcolor: 'background.paper', px: 1, py: 0.25, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
              >
                {t('processes:flowEditor.toolbar.nodesEdges', { nodes: nodes.length, edges: edges.length })}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ValidateIcon />}
                onClick={handleValidate}
                sx={{ pointerEvents: 'auto', textTransform: 'none', bgcolor: 'background.paper' }}
              >
                {t('processes:flowEditor.toolbar.validate')}
              </Button>
              {!readOnly && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                  onClick={handleSave}
                  disabled={saving || !dirty}
                  sx={{ pointerEvents: 'auto', textTransform: 'none' }}
                >
                  {t('processes:flowEditor.toolbar.save')}
                </Button>
              )}
            </Stack>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu} PaperProps={{ sx: { borderRadius: 2, minWidth: 260, mt: 1 } }}>
              {INSERTABLE_SPECS.map((spec) => (
                <MenuItem key={spec.type} onClick={() => { if (edgeForMenuId) addFlowNode(edgeForMenuId, spec); closeMenu(); }}>
                  <ListItemIcon><Avatar sx={{ width: 32, height: 32, bgcolor: `${spec.color}20`, color: spec.color }}>{getIcon(spec.icon)}</Avatar></ListItemIcon>
                  <ListItemText primary={t(`processes:flowEditor.nodeSpecs.${spec.type}`)} secondary={t(`processes:flowEditor.nodeSpecs.${spec.type}Desc`)} primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }} secondaryTypographyProps={{ fontSize: '0.8rem' }} />
                </MenuItem>
              ))}
            </Menu>
          </Paper>
        </Grid>

        {selectedNode && (
          <Grid item xs={12} md={4} sx={{ height: '100%', minHeight: 0 }}>
            {/* `overflow: hidden` + `minHeight: 0` on the column, so the ONLY scroll
                container in this panel is its body below. Without them the Grid item
                and the fieldset both scrolled and the owner saw two scrollbars side
                by side (screenshot 2026-08-23 19:46). */}
            <Paper sx={{ p: 2, height: '100%', minHeight: 0, overflow: 'hidden', borderRadius: 2, border: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={700}>{t('processes:flowEditor.properties.title')}</Typography>
                <IconButton size="small" onClick={() => setSelectedNodeId(null)}><CloseIcon /></IconButton>
              </Box>
              <Box component="fieldset" disabled={readOnly} aria-readonly={readOnly} sx={{ ...PANEL_SCROLL_SX, opacity: readOnly ? 0.8 : 1 }}>
                <Stack spacing={1.5}>
                  <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: (selectedNode.data.spec as NodeSpec).color }}>{getIcon((selectedNode.data.spec as NodeSpec).icon)}</Avatar>
                      <Typography variant="subtitle2" fontWeight={600}>{t(`processes:flowEditor.nodeSpecs.${selectedNode.data.nodeType}`)}</Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">{t(`processes:flowEditor.nodeSpecs.${selectedNode.data.nodeType}Desc`)}</Typography>
                  </Box>

                  <TextField
                    label={t('processes:flowEditor.properties.displayName')}
                    value={selectedNode.data.label}
                    onChange={(event) => {
                      markDirty();
                      setNodes((current) => current.map((node) => node.id === selectedNodeId ? { ...node, data: { ...node.data, label: event.target.value, displayLabel: undefined } } : node));
                    }}
                    fullWidth
                    size="small"
                    disabled={selectedNode.data.nodeType === 'start' || selectedNode.data.nodeType === 'end'}
                    helperText={t('processes:flowEditor.properties.displayNameHelper')}
                  />

                  <ExecutionRuleFields
                    nodeType={selectedNode.data.nodeType}
                    config={selectedConfig}
                    formSchema={definition.form_schema}
                    readOnly={readOnly}
                    onChange={updateSelectedConfig}
                  />
                </Stack>
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

interface FlowEditorProps {
  definitionId: string;
  onBack: () => void;
  onSaved?: (updated: ProcessDefinition) => void;
  onDirtyChange?: (dirty: boolean) => void;
}

export default function FlowEditor({ definitionId, onBack, onSaved, onDirtyChange }: FlowEditorProps) {
  const { t } = useTranslation();
  const [definition, setDefinition] = useState<ProcessDefinition | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const loaded = await processesApi.processDefinitionsApi.get(definitionId);
        if (!cancelled) setDefinition(loaded);
      } catch (caught) {
        if (!cancelled) {
          setError(caught instanceof ApiError
            ? translateApiError(caught, t)
            : t('processes:flowEditor.loadError'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [definitionId, t]);

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  if (error || !definition) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={<Button onClick={onBack}>{t('common:actions.back')}</Button>}>
          {error ?? t('processes:flowEditor.notFound')}
        </Alert>
      </Box>
    );
  }

  return (
    <ReactFlowProvider>
      <Canvas
        definition={definition}
        onBack={onBack}
        onSaved={(updated) => {
          setDefinition(updated);
          onSaved?.(updated);
        }}
        onDirtyChange={onDirtyChange}
      />
    </ReactFlowProvider>
  );
}
